import { useCallback, useContext, useEffect, useState } from "react";
import { CreateContext } from "../../../contexts/CreateContext";
import { StepNavContainer } from "../Navigation/StepNavContainer";
import { UploadProgressBar } from "../UploadProgressBar/UploadProgressBar";
import { NextButton } from "../Navigation/NextButton";
import { PrevButton } from "../Navigation/PrevButton";
import { UploadPreview } from "../Preview/UploadPreview";
import { completeGallery, completeUpload } from "@jconradi/tutu-porn-api-client/src/upload";
import { TagsSelectForm } from "../Tags/TagsSelectForm";
import { UploadItem } from "../../UploadDropzone/uploadManager";
import { ErrorMessage } from "../../Form/Inputs/ErrorMessage";
import useTranslation from "next-translate/useTranslation";

const MinTags = 3;
const MaxTags = 20;

export const InfoStep = () => {
    const { uploadManager, reset, setStep, galleryItems, price, setPrice, isPaid,
         setIsPaid, galleryPreviewItems, setGalleryPreviewItems, tags, setTags, setCreatedMedia } = useContext(CreateContext);
    const [ canFinish, setCanFinish ] = useState(uploadManager.isComplete());
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState<string>();
    const [ tagError, setTagError ] = useState<string>();
    const { t } = useTranslation('create');

    const frontUpload = galleryItems[0];
    const isGallery = galleryItems.length > 1;
    let titleText;

    const onBack = useCallback(async () => {
        const uploads = uploadManager.getUploads().length;

        if (uploads === 1) {
            await reset();
        }
        else {
            setStep("gallery");
        }
    }, [uploadManager]);

    const validate = () => {
        if (tags.length < 3) {
            setTagError(t('infoStep.errors.minTags'))
            return false;
        }
        if (tags.length > 10) {
            setTagError(t('infoStep.errors.maxTags'));
            return false;
        }

        setTagError(undefined);

        return true;
    }

    const onNext = async () => {
        if (loading) {
            return;
        }

        if (!validate()) {
            return;
        }

        try {
            setError(undefined);
            setLoading(true)
            await onFinish();

            setStep("complete");
        }
        finally {
            setLoading(false);
        }
    }

    const onFinish = async () => {
        const keepAudio = true;

        try {
            if (isGallery) {
                const media = await completeGallery({
                    isPaid,
                    price: isPaid ? price : undefined,
                    keepAudio,
                    uploads: galleryItems.map(u => u.uploadItem?.remoteUploadId!),
                    tags
                });
                setCreatedMedia(media.data);
            }
            else {
                const media = await completeUpload(uploadManager.getUpload(frontUpload.id).remoteUploadId!, {
                    tags,
                    isPaid,
                    price: isPaid ? price : undefined,
                    keepAudio
                });
                setCreatedMedia(media.data);
            }
        }
        catch (err) {
            console.error(err);
            setError(t('infoStep.errors.uploadError'));
            throw err;
        }
    }

    const onUploadUpdated = (upload: UploadItem) => {
        setCanFinish(false);
    }

    const onUploadsCompleted = () => {
        setCanFinish(true);
    };

    useEffect(() => {
        if (tags.length > 0) {
            validate();
        }
    }, [tags]);

    useEffect(() => {
        uploadManager.UploadUpdated.on(onUploadUpdated);
        uploadManager.UploadsCompleted.on(onUploadsCompleted);

        return () => {
            uploadManager.UploadUpdated.off(onUploadUpdated);
            uploadManager.UploadsCompleted.off(onUploadsCompleted);
        }

    }, []);

    if (!frontUpload) {
        return null;
    }
    
    if (isGallery) {
        titleText = t('infoStep.newGallery');
    }
    else {
        if (frontUpload.coverType == "photo") {
            titleText = t('infoStep.newPhoto');
        }
        else {
            titleText = t('infoStep.newVideo');
        }
    }

    return (
        <>
            <StepNavContainer>
                <div className="flex flex-row">
                    <PrevButton disabled={loading} onPrev={onBack} />
                    <h3 className="flex-1 text-right text-xl">{titleText}</h3>
                </div>
            </StepNavContainer>
            <div className="flex flex-col sm:flex-row gap-x-16">
                <div className={`flex-1 `}>
                    <UploadPreview className={`rounded-[16px] ${isGallery ? "gallery rounded-2xl" : ""}`}
                     cover={frontUpload.uploadItem!.poster} coverType={frontUpload.coverType} />
                </div>
                <div className="flex-1">
                    <TagsSelectForm tags={tags} setTags={setTags} />

                    {tagError && <ErrorMessage message={tagError} />}
                    {/* <IsPaidSelector isPaid={isPaid} setIsPaid={setIsPaid} />
                    {isPaid && <>
                        <NumericInput prefix={'$'} amount={price} setAmount={setPrice} minAmount={5} maxAmount={500} />
                        <NumericInput amount={galleryPreviewItems} setAmount={setGalleryPreviewItems} minAmount={1} maxAmount={galleryItems.length - 1} />

                        <VerificationRequiredNotification />
                    </>} */}
                    <UploadProgressBar />
                    {error &&
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <span className="text-secondary text-base text-center">{error}</span>
                        </div>
                    }
                </div>
            </div>
            <StepNavContainer className="w-full flex justify-end mt-8">
                <NextButton loading={loading} disabled={!canFinish} onNext={onNext} label={t('finish')} />
            </StepNavContainer>
        </>
    );
}